import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription
} from "@/components/ui/sheet";
import { Menu, ShieldCheck, FileText, Calculator, BookOpen, MessageCircle, ChevronRight, Sparkles } from "lucide-react";

const linkBase = "text-sm font-medium text-muted-foreground hover:text-foreground transition-colors";
const linkActive = "text-foreground";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = [
    { to: "/pricing", label: "Pricing" },
    { to: "/calculator", label: "Tax Calculator" },
    { to: "/knowledge", label: "Knowledge" },
    { to: "/roadmap", label: "Roadmap" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground font-display font-bold text-base shadow-sm group-hover:scale-[1.03] transition-transform">
            R
          </div>
          <div className="leading-tight">
            <div className="font-display text-[15px] font-bold tracking-tight">CA Ritika Singh</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">AI Tax Filing</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {items.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.slice(1)}`}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {!user ? (
            <>
              <Button asChild variant="ghost" data-testid="nav-login-btn">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild data-testid="nav-signup-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/signup">
                  Start filing
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" data-testid="nav-dashboard-btn">
                <Link to={user.role === "admin" ? "/admin" : user.role === "ca" ? "/ca-portal" : "/dashboard"}>
                  Dashboard
                </Link>
              </Button>
              <Button onClick={() => { logout(); navigate("/"); }} variant="outline" data-testid="nav-logout-btn">
                Sign out
              </Button>
            </>
          )}
        </div>

        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="nav-mobile-trigger" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <SheetHeader>
                <SheetTitle className="font-display">CA Ritika Singh</SheetTitle>
                <SheetDescription>AI-powered Income Tax filing</SheetDescription>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {items.map(it => (
                  <Link key={it.to} to={it.to} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-muted">
                    {it.label}
                  </Link>
                ))}
                <div className="border-t border-border my-3" />
                {!user ? (
                  <>
                    <Button asChild variant="outline" className="justify-start"><Link to="/login">Sign in</Link></Button>
                    <Button asChild className="justify-start mt-2"><Link to="/signup">Create account</Link></Button>
                  </>
                ) : (
                  <>
                    <Button asChild className="justify-start"><Link to="/dashboard">Dashboard</Link></Button>
                    <Button variant="outline" className="justify-start mt-2" onClick={() => { logout(); navigate("/"); }}>Sign out</Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
